import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Spacer } from "components/general/Spacer/Spacer";
import { Text } from "components/general/Text";
import { motion } from "framer-motion";
import { useIsDesktop } from "hooks/useDeviceType";
import { throttle } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { RulerPip } from "./RulerPip";
import { useEffectOnce } from "usehooks-ts";

interface RulerProps {
  value: number;
  setValue: React.Dispatch<React.SetStateAction<number>> | ((value: number) => void);
  minValue?: number;
  maxValue?: number;
  color: string;
  isLocked: boolean;
}

export const Ruler = ({
  value,
  setValue,
  minValue = 0,
  maxValue = 100,
  color,
  isLocked,
}: RulerProps) => {
  const { theme, makerTokens } = GlobalStore.useState(s => ({
    theme: s.theme,
    makerTokens: s.makerTokens,
  }));
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrollPositioning = useRef(false);
  const positionScrollTries = useRef(0);
  const positionScrollTimeoutId = useRef(-1);
  const RULER_LABEL_BOTTOM_OFFSET = useRulerLabelBottomOffsetConfig();
  const isLockedRef = useRef(isLocked);

  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  // Input field
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // For dragging
  const mouseDownDetails = useRef<{
    isMouseDown: boolean;
    startX: null | number;
    startScrollLeft: null | number;
    direction: null | "left" | "right";
    startTime: null | Date;
  }>({
    isMouseDown: false,
    startX: null,
    startScrollLeft: null,
    direction: null,
    startTime: null,
  });

  const getLineColor = useCallback(
    (fifth: boolean, i: number) => {
      if (i <= value) return color;
      return fifth ? theme.font.primary.default : theme.theme["1200"];
    },
    [color, theme.font.primary.default, theme.theme, value]
  );
  const getHeight = useCallback((tenth: boolean) => {
    return tenth ? 20 : 16;
  }, []);

  const getMaxScrollLeftOffset = useCallback(() => {
    if (scrollRef.current == null) {
      return 0;
    }
    return scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
  }, []);

  // Ensure slider's middle marker is seen in the center of this container
  const [isMoving, setIsMoving] = useState(false);
  const isMovingTimeoutId = useRef(-1);

  // Dragging functions
  const dragStart = useCallback((e: MouseEvent) => {
    if (isLockedRef.current) return;
    mouseDownDetails.current.isMouseDown = true;
    mouseDownDetails.current.startX = e.pageX;
    mouseDownDetails.current.startTime = new Date();
    mouseDownDetails.current.startScrollLeft = scrollRef.current?.scrollLeft ?? null;
  }, []);

  // const lastDragStopTimeoutId = useRef(-1);
  const dragStop = useCallback(() => {
    if (isLockedRef.current) return;
    mouseDownDetails.current.isMouseDown = false;
  }, []);

  const dragMove = useCallback((e: MouseEvent) => {
    e.preventDefault();
    if (
      !mouseDownDetails.current.isMouseDown ||
      mouseDownDetails.current.startX == null ||
      mouseDownDetails.current.startScrollLeft == null ||
      scrollRef.current == null ||
      isLockedRef.current
    ) {
      return;
    }
    const scroll = e.pageX - mouseDownDetails.current.startX;
    scrollRef.current.scrollLeft = mouseDownDetails.current.startScrollLeft - scroll;
  }, []);

  const maxPossibleValueWithoutLocks = 101 - makerTokens.length;
  const isMaxPossibleValueWithoutLocks = maxPossibleValueWithoutLocks === maxValue;
  const maxValueDisplayedInSlider = Math.min(maxValue + 14, 101) - 1;

  const positionScrollCallback = useCallback(
    (scrollToValue?: number) => {
      positionScroll(
        scrollToValue ?? value,
        positionScrollTries,
        isMoving,
        setIsMoving,
        scrollRef,
        isMovingTimeoutId,
        minValue,
        maxValue,
        getMaxScrollLeftOffset()
      );
    },
    [getMaxScrollLeftOffset, isMoving, maxValue, minValue, value]
  );

  useEffectOnce(() => {
    // Initialize drag interactivity
    const resizeCallback = () => {
      positionScrollTries.current = 0;
      positionScrollCallback();
    };
    window.addEventListener("resize", resizeCallback);
    window.addEventListener("mousemove", dragMove, false);
    window.addEventListener("mousedown", dragStart, false);
    window.addEventListener("mouseup", dragStop, false);
    window.addEventListener("mouseleave", dragStop, false);

    // Initialize initial scroll position
    positionScrollCallback();

    return () => {
      window.removeEventListener("resize", resizeCallback);
      window.removeEventListener("mousemove", dragMove);
      window.removeEventListener("mousedown", dragStart);
      window.removeEventListener("mouseup", dragStop);
      window.removeEventListener("mouseleave", dragStop);
    };
  });

  useEffect(() => {
    if (isScrollPositioning.current || isMoving) {
      GlobalStore.update(s => {
        s.isSliderMoving = true;
      });
    } else {
      GlobalStore.update(s => {
        s.isSliderMoving = false;
      });
    }
  }, [isMoving]);

  const confirmValue = useCallback(() => {
    if (scrollRef.current == null || isScrollPositioning.current || isLockedRef.current) {
      return;
    }
    const percentage =
      Math.round(
        (scrollRef.current.scrollLeft / getMaxScrollLeftOffset()) * (maxValue - minValue)
      ) + minValue;
    const controlledPercentage = Math.max(minValue, Math.min(maxValue, percentage));
    setValue(controlledPercentage);

    // Only re-position scrollbar after scroll has ended, or jank to force
    // fit % marker to an integer will appear mid-scroll
    if (positionScrollTimeoutId.current !== -1) {
      window.clearTimeout(positionScrollTimeoutId.current);
      positionScrollTimeoutId.current = -1;
    }
    if (isScrollPositioning.current) {
      return;
    }
    setIsMoving(true);

    positionScrollTimeoutId.current = window.setTimeout(() => {
      isScrollPositioning.current = true;
      positionScrollCallback(controlledPercentage);
      window.setTimeout(() => {
        isScrollPositioning.current = false;
      }, 50);
      positionScrollTimeoutId.current = -1;
    }, 100);
  }, [getMaxScrollLeftOffset, maxValue, minValue, positionScrollCallback, setValue]);

  const basePaddingOffsetPct = 47;
  const maxLeftPaddingOffsetPct = Math.max(0, basePaddingOffsetPct - minValue * 3.5);
  const maxRightPaddingOffsetPct = Math.max(0, basePaddingOffsetPct - (100 - maxValue) * 3);

  return (
    <RulerContainer>
      <RulerPip
        inputRef={inputRef}
        isInputFocused={isInputFocused}
        setIsInputFocused={setIsInputFocused}
        value={value}
        setValue={setValue}
        color={color}
        isRulerMoving={isMoving}
        positionScroll={positionScrollCallback}
      />
      <Spacer height={10} />

      <div style={{ position: "absolute", height: 0, display: "flex", alignItems: "end" }}>
        <RulerPipContainer
          color={color}
          animate={{
            height: 25 + (isMoving || isInputFocused ? MARKER_SHIFT_UP_HEIGHT : 0),
          }}
          transition={RULER_TRANSITION_ANIMATION}
        />
      </div>

      {isLocked && (
        <Overlay
          onScroll={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}
      <RulerMarkerContainer
        ref={scrollRef}
        onScroll={confirmValue}
        style={{ left: !isMaxPossibleValueWithoutLocks ? 1 : 0 }}
      >
        <div style={{ flex: `0 0 ${maxLeftPaddingOffsetPct}%` }} />
        {Array(maxValueDisplayedInSlider + 1)
          .fill(undefined)
          .map((_, i, arr) => {
            const fifth = i % 5 === 0;
            const tenth = i % 10 === 0;
            return (
              <>
                <div
                  key={i}
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: getLineColor(fifth, i),
                      height: getHeight(tenth),
                      width: 1,
                      marginBottom: 10,
                    }}
                  />
                  {tenth && (
                    <Text
                      as="p"
                      textStyle="Paragraph"
                      size="Medium"
                      color={getLineColor(fifth, i)}
                      textCss={css`
                        position: absolute;
                        bottom: ${-RULER_LABEL_BOTTOM_OFFSET}px;
                        width: 50px;
                        text-align: center;
                      `}
                    >
                      {i}
                    </Text>
                  )}
                </div>
                {i === arr.length - 1 && !isMaxPossibleValueWithoutLocks && (
                  <Spacer minWidth={"0.1%"} />
                )}
              </>
            );
          })}
        <div
          style={{
            visibility: "hidden",
            whiteSpace: "nowrap",
            width: 0,
            height: 0,
            flex: `0 0 ${maxRightPaddingOffsetPct}%`,
          }}
        >
          {" "}
          :)
        </div>
      </RulerMarkerContainer>
    </RulerContainer>
  );
};

// Sub-components

const RulerContainer = styled.div(() => {
  return {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
  };
});

const RulerMarkerContainer = styled.div(() => {
  const RULER_LABEL_BOTTOM_OFFSET = useRulerLabelBottomOffsetConfig();
  return {
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "end",
    gap: 10,
    overflowY: "hidden",
    overflowX: "scroll",
    userSelect: "none",
    paddingBottom: RULER_LABEL_BOTTOM_OFFSET,

    // Hide scrollbar
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE and Edge
    "::-webkit-scrollbar": {
      // WebKit-based browsers
      display: "none",
    },
  };
});

const Overlay = styled.div(() => {
  return {
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 5,
    overflow: "hidden",
  };
});

const RulerPipContainer = styled(motion.div)(({ color }: { color: string }) => {
  const isDesktop = useIsDesktop();
  const bottom = useMemo(() => {
    if (!isDesktop) return -24;
    return -28;
  }, [isDesktop]);
  return {
    position: "relative",
    width: 3,
    minWidth: 3,
    background: color,
    bottom,
    zIndex: 1,
  };
});

// Helpers

const useRulerLabelBottomOffsetConfig = () => {
  const isDesktop = useIsDesktop();
  if (isDesktop) return 10;
  return 9;
};

export const MARKER_SHIFT_UP_HEIGHT = 23.5;

export const RULER_TRANSITION_ANIMATION = { type: "spring", damping: 20, mass: 1, stiffness: 300 };

const positionScroll = throttle(
  (
    scrollToValue: number,
    positionScrollTries: React.MutableRefObject<number>,
    isMoving: boolean,
    setIsMoving: React.Dispatch<React.SetStateAction<boolean>>,
    scrollRef: React.RefObject<HTMLDivElement>,
    isMovingTimeoutId: React.MutableRefObject<number>,
    minValue: number,
    maxValue: number,
    maxScrollLeftOffset: number
    // additionalSliderOffsetPx: number
  ) => {
    if (positionScrollTries.current > 5) return;

    if (maxScrollLeftOffset) {
      window.clearTimeout(isMovingTimeoutId.current);

      const finalScrollPosition = (
        ((scrollToValue - minValue) / (maxValue - minValue)) *
        maxScrollLeftOffset
      ).toFixed(2);
      scrollRef.current?.scrollTo({
        left: Number(finalScrollPosition),
      });

      isMovingTimeoutId.current = window.setTimeout(() => {
        setIsMoving(false);
        isMovingTimeoutId.current = -1;
      }, 300);
    } else {
      setTimeout(() => {
        positionScrollTries.current++;
        positionScroll(
          scrollToValue,
          positionScrollTries,
          isMoving,
          setIsMoving,
          scrollRef,
          isMovingTimeoutId,
          minValue,
          maxValue,
          maxScrollLeftOffset
        );
      }, 100);
    }
  },
  200
);
